import { Box, Button, Chip, Typography } from '../ui';

interface QuizResultsProps {
  score: number;
  passed: boolean;
  totalQuestions: number;
  correctAnswers: number;
  passThreshold: number;
  /** When false, a failing score still completes the lesson, so the
   *  fail state offers "Continue" alongside "Retake". Default true. */
  requirePass?: boolean;
  onRetake: () => void;
  onCourseRefresh: () => void;
}

export default function QuizResults({
  score,
  passed,
  totalQuestions,
  correctAnswers,
  passThreshold,
  requirePass = true,
  onRetake,
  onCourseRefresh,
}: QuizResultsProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {/* Score card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          border: '1px solid',
          borderColor: passed ? 'success.main' : 'error.main',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Colored header band */}
        <Box
          sx={{
            bgcolor: passed ? 'success.main' : 'error.main',
            color: 'white',
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="h2" fontWeight="bold" color="inherit">
            {Math.round(score)}%
          </Typography>
          <Chip
            label={passed ? 'Passed' : 'Failed'}
            sx={{
              fontWeight: 600,
              fontSize: 14,
              px: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
            }}
          />
        </Box>

        {/* Details section */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body1" color="text.primary" fontWeight={500}>
            {correctAnswers} out of {totalQuestions} correct
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pass threshold: {passThreshold}%
          </Typography>

          {passed ? (
            <>
              <Typography variant="body1" color="success.main" fontWeight={500} sx={{ mt: 1 }}>
                Congratulations! You passed the quiz.
              </Typography>
              <Button variant="contained" color="primary" onClick={onCourseRefresh} sx={{ mt: 1 }}>
                Continue
              </Button>
            </>
          ) : requirePass ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                You need {passThreshold}% to pass. Try again!
              </Typography>
              <Button variant="contained" color="primary" onClick={onRetake} sx={{ mt: 1 }}>
                Retake Quiz
              </Button>
            </>
          ) : (
            // Not required to pass — the lesson is already complete, so the
            // learner can move on or retake to improve their score.
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This quiz doesn't require a passing score, so you're all set. You
                can retake it to improve your result.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button variant="outlined" color="primary" onClick={onRetake}>
                  Retake Quiz
                </Button>
                <Button variant="contained" color="primary" onClick={onCourseRefresh}>
                  Continue
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
